import { useCallback, useRef } from "react";
import { Plus, Upload, FileText, Download, Trash2, Paperclip } from "lucide-react";
import { FormLabel } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { TransactionAttachment, AttachmentForm, AttachmentType } from "@/types/transaction";
import { AttachmentRow } from "./AttachmentRow";
import { DEFAULT_ATTACHMENT_FORM, DEFAULT_ATTACHMENT_TYPE } from "@/constants/attachments";

export interface NewAttachment {
  file: File;
  description: string;
  attachment_form: AttachmentForm;
  attachment_type: AttachmentType;
}

interface AttachmentsSectionProps {
  attachments: NewAttachment[];
  setAttachments: (attachments: NewAttachment[]) => void;
  existingAttachments?: TransactionAttachment[];
  handleDeleteExistingAttachment?: (attachment: TransactionAttachment) => Promise<void> | void;
  handleDownloadAttachment?: (attachment: TransactionAttachment) => Promise<void> | void;
  onExistingAttachmentDescriptionChange?: (id: string, description: string) => void;
  isViewOnly?: boolean;
}

const formatBytes = (size?: number | null) => {
  if (!size && size !== 0) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
};

export function AttachmentsSection({
  attachments,
  setAttachments,
  existingAttachments = [],
  handleDeleteExistingAttachment,
  handleDownloadAttachment,
  onExistingAttachmentDescriptionChange,
  isViewOnly = false,
}: AttachmentsSectionProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const openFileDialog = useCallback(() => {
    if (isViewOnly) return;
    const input = fileInputRef.current;
    if (!input) return;

    try {
      if (typeof input.showPicker === 'function') {
        input.showPicker();
      } else {
        input.click();
      }
    } catch {
      input.click();
    }
  }, []);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isViewOnly) return;
    const files = event.target.files;
    if (!files) return;

    const acceptedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf', 'text/plain', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    const next: NewAttachment[] = [...attachments];

    Array.from(files).forEach((file) => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`Arquivo ${file.name} excede o limite de 5MB`);
        return;
      }

      if (!acceptedTypes.includes(file.type)) {
        toast.error(`Tipo de arquivo não suportado: ${file.type}`);
        return;
      }

      next.push({
        file,
        description: '',
        attachment_form: DEFAULT_ATTACHMENT_FORM,
        attachment_type: DEFAULT_ATTACHMENT_TYPE,
      });
    });

    setAttachments(next);

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeNewAttachment = (index: number) => {
    if (isViewOnly) return;
    const next = attachments.filter((_, i) => i !== index);
    setAttachments(next);
  };

  const updateAttachment = (
    index: number,
    updates: Partial<Omit<NewAttachment, 'file'>>
  ) => {
    if (isViewOnly) return;
    const next = attachments.map((att, i) =>
      i === index ? { ...att, ...updates } : att
    );
    setAttachments(next);
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <FormLabel className="text-sm font-medium">Anexos e Documentos</FormLabel>
        <span className="text-xs text-muted-foreground">
          {attachments.length + existingAttachments.length} anexo(s)
        </span>
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileSelect}
        accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.txt,.doc,.docx,.xls,.xlsx"
        className="sr-only"
        disabled={isViewOnly}
      />

      {/* Grid Header - Desktop Only */}
      <div className="hidden md:grid md:grid-cols-[140px_1fr_160px_1fr_40px] gap-3 px-2 pb-1 border-b border-border">
        <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
          Forma do anexo
        </div>
        <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
          Anexo
        </div>
        <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
          Tipo de anexo
        </div>
        <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
          Descrição
        </div>
        <div></div>
      </div>

      {/* New Attachments */}
      {attachments.length > 0 && (
        <div className="space-y-2">
          {attachments.map((attachment, index) => (
            <AttachmentRow
              key={index}
              file={attachment.file}
              fileName={attachment.file.name}
              fileSize={attachment.file.size}
              form={attachment.attachment_form}
              type={attachment.attachment_type}
              description={attachment.description}
              onFormChange={(value) => updateAttachment(index, { attachment_form: value })}
              onTypeChange={(value) => updateAttachment(index, { attachment_type: value })}
              onDescriptionChange={(value) => updateAttachment(index, { description: value })}
              onRemove={() => removeNewAttachment(index)}
              disabled={isViewOnly}
            />
          ))}
        </div>
      )}

      {/* Add Attachment Button */}
      <Button
        type="button"
        variant="outline"
        onClick={openFileDialog}
        className="w-full h-9 border-2 border-dashed border-muted-foreground/25 hover:border-muted-foreground/50 hover:bg-muted/25 transition-all duration-200"
        disabled={isViewOnly}
      >
        <div className="flex items-center justify-center gap-2">
          <Plus className="h-3.5 w-3.5" />
          <span className="text-sm font-medium">Adicionar anexo</span>
        </div>
      </Button>

      {/* Upload Info */}
      <div className="flex items-center gap-2 text-[10px] text-muted-foreground px-1">
        <Upload className="h-3 w-3" />
        <span>Máximo 5MB por arquivo • PDF, DOC, XLS, Imagens</span>
      </div>

      {/* Existing Attachments Section */}
      {existingAttachments && existingAttachments.length > 0 && (
        <div className="pt-3 space-y-2 border-t border-border">
          <div className="flex items-center gap-2">
            <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
            <h4 className="text-xs font-semibold text-foreground">
              Anexos salvos ({existingAttachments.length})
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {existingAttachments.map((att) => (
              <div
                key={att.id}
                className="p-2 rounded-lg border bg-card/40 shadow-sm hover:border-primary/50 transition"
              >
                <div className="flex items-start gap-2">
                  <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center text-primary">
                    <FileText className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1 min-w-0 space-y-0.5">
                    <p className="text-xs font-semibold truncate" title={att.file_name}>
                      {att.file_name}
                    </p>
                    <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
                      {att.attachment_type && (
                        <span className="inline-flex px-1.5 py-0.5 rounded-full bg-muted uppercase tracking-wide">
                          {att.attachment_type}
                        </span>
                      )}
                      {att.attachment_form && (
                        <span className="inline-flex px-1.5 py-0.5 rounded-full bg-muted uppercase tracking-wide">
                          {att.attachment_form}
                        </span>
                      )}
                      {att.file_type && <span>{att.file_type}</span>}
                      {att.file_size ? <span>• {formatBytes(att.file_size)}</span> : null}
                    </div>
                    {att.description && (
                      <p className="text-[10px] text-muted-foreground">{att.description}</p>
                    )}
                    {att.created_at && (
                      <p className="text-[10px] text-muted-foreground">
                        Enviado em {new Date(att.created_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-1 justify-end">
                  {handleDownloadAttachment && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownloadAttachment(att)}
                      className="h-7 px-2 text-xs gap-1"
                    >
                      <Download className="h-3 w-3" />
                      Baixar
                    </Button>
                  )}
                  {handleDeleteExistingAttachment && !isViewOnly && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteExistingAttachment(att)}
                      className="h-7 px-2 text-xs text-destructive hover:bg-red-50 gap-1"
                    >
                      <Trash2 className="h-3 w-3" />
                      Remover
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
